import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { strings } from '@constants'
import { getDashboardData, getOrganisationDashboardData } from '@data'

import { Devices, Spacings } from '@styles'
import { Pill, BarChart, AreaChart, PieChart, DashboardRecord, EmptyState } from '@base'
import { decimalSeparatedNumber, displayAmount, processData } from '@utils'
import { Wrapper, ItemWrapper, ItemHeader, AccumulatedDataWrapper, LoaderWrapper } from './styles'
import { ListAlt, LocalMallOutlined, AccountBalance as AccountBalanceIcon, List } from '@material-ui/icons'
import { isBuyer, isSupplier } from 'src/utils/adminFilter'

const MainWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(1, 1fr);

	@media ${Devices.tablet} {
		grid-template-columns: repeat(3, 3fr);
		grid-column-gap: ${Spacings.SPACING_4B};
	}
	@media ${Devices.laptop} {
		grid-template-columns: repeat(3, 3fr);
	}
`

export const AccumulatedData = ({ getter, data }) => {
	if (!getter || !data) return
	return (
		<AccumulatedDataWrapper>
			{getter.map((item, index) => {
				const _data = data?.[item.getterKey] ?? {}
				let _unformatedValue = _data?.[item.key] / (item?.valueDivisor ?? 1)

				if (isNaN(_unformatedValue)) _unformatedValue = 0

				const _value = `${item.formatter ? item.formatter(_unformatedValue) : _unformatedValue} ${item.unit ?? ''}`
				return (
					<Pill
						key={`data-${item.getterKey}}${index}`}
						label={item.label}
						value={_value}
						margin={`0 ${Spacings.SPACING_1B} ${Spacings.SPACING_2B} ${Spacings.SPACING_2B}`}
					/>
				)
			})}
		</AccumulatedDataWrapper>
	)
}

export const AnalyticsLoader = ({ data }) =>
	(!data || !data?.length) && (
		<LoaderWrapper>
			<EmptyState text='No charts to display' icon={<List />} noAction />
		</LoaderWrapper>
	)

export const RenderCharts = ({ data, horizontal, vertical, showSideMenu }) =>
	data.map((data, index) => {
		if (data.chartType === 'bar') {
			return (
				<ItemWrapper key={`${data.chartType}-${index}`} fullWidth={horizontal} fullHight={vertical} showSideMenu={showSideMenu}>
					<ItemHeader>{data.header}</ItemHeader>
					<BarChart data={data} />
				</ItemWrapper>
			)
		} else if (data.chartType === 'line') {
			return (
				<ItemWrapper key={`${data.chartType}-${index}`} fullWidth={horizontal} fullHight={vertical} showSideMenu={showSideMenu}>
					<ItemHeader>{data.header}</ItemHeader>
					<AccumulatedData getter={data?.summary} data={data?.accumulatedData} />
					<AreaChart data={data} />
				</ItemWrapper>
			)
		} else if (data.chartType === 'pie' && data.numberOfItems != 0) {
			return (
				<ItemWrapper key={`${data.chartType}-${index}`} fullWidth={horizontal} fullHight={vertical} showSideMenu={showSideMenu}>
					<ItemHeader>{data.header}</ItemHeader>
					{/* <AccumulatedData getter={data?.summary} data={data?.accumulatedData} /> */}
					<PieChart data={data} />
				</ItemWrapper>
			)
		}
		return null
	})

const Dashboard = ({ userData, inProfileView, isAdmin, userOrgData = null, orgId = null }) => {
	let user = useSelector((state) => state.getIn(['user', 'user']))
	const showSideMenu = useSelector((state) => state.getIn(['root', 'showSideMenu']))

	//
	let loggedInOrg = useSelector((state) => state.getIn(['user', 'organisation']))
	let organisation = loggedInOrg || userOrgData

	user = userData || user

	const [chartStats, setChartStats] = useState([])
	const [records, SetRecord] = useState()

	// const onLoadBreadcrunbs = inProfileView ? editUserProfileBreadcrumb(user?.user_id) : { showNewsInHeader: true }
	// useHeaderMeta(onLoadBreadcrunbs, !inProfileView)

	const checkOnlySupplier = (allEnquiries) => {
		if (isBuyer(organisation?.roles) || !allEnquiries) {
			return false
		}
		const tempAllWeight = allEnquiries.reduce(
			(total, cur) => {
				total.weight += cur.weight
				return total
			},
			{ weight: 0 }
		)
		return tempAllWeight.weight === 0
	}

	const createChartStatsData = (data) => {
		const newData = []
		if (isSupplier(organisation.roles)) {
			newData.push(
				processData(
					[
						{
							data: data.total_quotations_submitted,
							key: 'quotes_submitted',
						},
						{
							data: data.quotations_converted_to_order,
							key: 'converted_order',
						},
					],
					{
						dataKey: 'weight',
						valueDivisor: 1000, // for converting to MT
						yLabel: strings('placeholder_weight_in_mt'),
						unit: 'MT',
						formatter: decimalSeparatedNumber,
						header: strings('monthly_quotes_conversion_chart'),
						chartType: 'bar',
					}
				)
			)
			newData.push(
				processData(
					[
						{
							data: data.total_sales,
							key: 'total_sales',
						},
					],
					{
						dataKey: 'amount',
						valueDivisor: 100000, // for converting to lacs
						yLabel: strings('amount_lakh'),
						unit: 'Lakh',
						formatter: displayAmount,
						header: strings('business_sales'),
						chartType: 'line',
						summary: [
							{
								getterKey: 'total_sales',
								label: `${strings('invoices')}:`,
								key: 'unit',
							},
							{
								getterKey: 'total_sales',
								label: `${strings('amount')}:`,
								key: 'amount',
								formatter: displayAmount,
								valueDivisor: 100000,
								unit: 'Lakh',
							},
						],
					}
				)
			)
			newData.push(
				processData(
					[
						{
							data: data.total_quotations_submitted,
							key: 'quotes_submitted',
						},
					],
					{
						dataKey: 'weight',
						valueDivisor: 1000, // for converting to MT
						yLabel: strings('placeholder_weight_in_mt'),
						unit: 'MT',
						formatter: decimalSeparatedNumber,
						header: strings('quotes_submitted'),
						chartType: 'line',
						summary: [
							{
								getterKey: 'quotes_submitted',
								label: `${strings('total')}:`,
								key: 'unit',
							},
							{
								getterKey: 'quotes_submitted',
								label: `${strings('weight')}:`,
								key: 'weight',
								formatter: decimalSeparatedNumber,
								valueDivisor: 1000,
								unit: 'MT',
							},
						],
					}
				)
			)
			newData.push(
				processData(
					[
						{
							data: data.quotations_converted_to_order,
							key: 'sale_order',
						},
					],
					{
						dataKey: 'weight',
						valueDivisor: 1000, // for converting to MT
						yLabel: strings('placeholder_weight_in_mt'),
						unit: 'MT',
						formatter: decimalSeparatedNumber,
						header: strings('sale_order'),
						chartType: 'line',
						summary: [
							{
								getterKey: 'sale_order',
								label: `${strings('total')}:`,
								key: 'unit',
							},
							{
								getterKey: 'sale_order',
								label: `${strings('weight')}:`,
								key: 'weight',
								formatter: decimalSeparatedNumber,
								valueDivisor: 1000,
								unit: 'MT',
							},
						],
					}
				)
			)
		}

		const onlySupplier = checkOnlySupplier(data.total_enquiries_made)
		if (!onlySupplier) {
			newData.push(
				processData(
					[
						{
							data: data.total_enquiries_made,
							key: 'enquiry',
						},
						{
							data: data.total_purchase_orders_given,
							key: 'order',
						},
					],
					{
						dataKey: 'weight',
						valueDivisor: 1000, // for converting to MT
						yLabel: strings('placeholder_weight_in_mt'),
						formatter: decimalSeparatedNumber,
						unit: 'MT',
						header: strings('monthly_conversion_chart'),

						chartType: 'bar',
					}
				)
			)
			newData.push(
				processData(
					[
						{
							data: data.total_purchase,
							key: 'business_purchase',
						},
					],
					{
						dataKey: 'amount',
						valueDivisor: 100000, // for converting to lacs
						yLabel: strings('amount_lakh'),
						unit: 'Lakh',
						formatter: displayAmount,
						header: strings('business_purch'),
						chartType: 'line',
						summary: [
							{
								getterKey: 'business_purchase',
								label: `${strings('invoices')}:`,
								key: 'unit',
							},
							{
								getterKey: 'business_purchase',
								label: `${strings('amount')}:`,
								key: 'amount',
								formatter: displayAmount,
								valueDivisor: 100000,
								unit: 'Lakh',
							},
						],
					}
				)
			)

			newData.push(
				processData(
					[
						{
							data: data.total_enquiries_made,
							key: 'enquiry',
						},
					],
					{
						dataKey: 'weight',
						valueDivisor: 1000, // for converting to MT
						yLabel: strings('placeholder_weight_in_mt'),
						unit: 'MT',
						formatter: decimalSeparatedNumber,
						header: strings('enquiries'),
						chartType: 'line',
						summary: [
							{
								getterKey: 'enquiry',
								label: `${strings('total')}:`,
								key: 'unit',
							},
							{
								getterKey: 'enquiry',
								label: `${strings('weight')}:`,
								key: 'weight',
								formatter: decimalSeparatedNumber,
								valueDivisor: 1000,
								unit: 'MT',
							},
						],
					}
				)
			)
			newData.push(
				processData(
					[
						{
							data: data.total_purchase_orders_given,
							key: 'purchase_order',
						},
					],
					{
						dataKey: 'weight',
						valueDivisor: 1000, // for converting to MT
						yLabel: strings('placeholder_weight_in_mt'),
						unit: 'MT',
						formatter: decimalSeparatedNumber,
						header: strings('purchase', 'orders'),
						chartType: 'line',
						summary: [
							{
								getterKey: 'purchase_order',
								label: `${strings('total')}:`,
								key: 'unit',
							},
							{
								getterKey: 'purchase_order',
								label: `${strings('weight')}:`,
								key: 'weight',
								formatter: decimalSeparatedNumber,
								valueDivisor: 1000,
								unit: 'MT',
							},
						],
					}
				)
			)
		}

		setChartStats(newData)
	}

	useEffect(() => {
		if (orgId) {
			getOrganisationDashboardData(orgId)
				.then((data) => {
					SetRecord(data)
					createChartStatsData(data)
				})
				.catch((err) => {})
		} else {
			getDashboardData(user?.user_id)
				.then((data) => {
					SetRecord(data)
					createChartStatsData(data)
				})
				.catch((err) => {})
		}
	}, [user?.user_id, orgId])

	return (
		<>
			<MainWrapper>
				<DashboardRecord
					title={isBuyer(organisation?.roles) ? 'Total Enquiry' : 'Total Quotation'}
					initialData={isBuyer(organisation?.roles) ? records?.total_enquiries_made : records?.total_quotations_submitted}
					key={isBuyer(organisation?.roles) ? 'total_enquiries_made' : 'total_quotations_submitted'}
					keyName={'weight'}
					icon={<ListAlt fontSize='medium' />}
				/>
				<DashboardRecord
					title={'Total Order'}
					initialData={isBuyer(organisation?.roles) ? records?.total_purchase_orders_given : records?.total_supply_orders_fulfilled}
					key={isBuyer(organisation?.roles) ? 'total_purchase_orders_given' : 'total_supply_orders_fulfilled'}
					keyName={'weight'}
					icon={<LocalMallOutlined fontSize='medium' />}
				/>
				<DashboardRecord
					title={'Total Invoices'}
					initialData={isBuyer(organisation?.roles) ? records?.total_purchase : records?.total_sales}
					key={isBuyer(organisation?.roles) ? 'total_purchase' : 'total_sales'}
					keyName={'amount'}
					icon={<AccountBalanceIcon fontSize='medium' />}
				/>
			</MainWrapper>
			<AnalyticsLoader data={chartStats} />
			<Wrapper>
				{/* <RenderCharts data={chartStats} horizontal={!showSideMenu} showSideMenu={showSideMenu} /> */}
				<RenderCharts data={chartStats} horizontal={false} showSideMenu={true} />
			</Wrapper>
		</>
	)
}

export default Dashboard
